import { generateUUID } from "@perceptron/utils/crypto";
import { getContentTypeFromExtension, getExtensionFromContentType } from "@perceptron/utils/mime";
import { MediaKind } from "@perceptron/utils/types";

import { ImageRenderer } from "@demo/components/Media/ImageRenderer";
import { MediaRendererComponent } from "@demo/components/Media/types";
import { VideoRenderer } from "@demo/components/Media/VideoRenderer";
import { MediaData } from "@demo/types/base";

interface MediaTypeInfo {
  mimeType: string;
  friendlyName: string;
}
export class MediaFactory {
  private static videoEnabled: boolean = true; // Video always enabled in demo app

  private static renderers: Record<MediaKind, MediaRendererComponent> = {
    [MediaKind.Image]: ImageRenderer,
    [MediaKind.Video]: VideoRenderer,
  };

  private static validators: Record<MediaKind, (file: File) => boolean> = {
    [MediaKind.Image]: (file) => file.type.startsWith("image/"),
    [MediaKind.Video]: (file) => file.type.startsWith("video/"),
  };

  private static acceptedMimeTypes: Record<MediaKind, MediaTypeInfo[]> = {
    [MediaKind.Image]: [
      { mimeType: "image/png", friendlyName: "png" },
      { mimeType: "image/jpeg", friendlyName: "jpeg" },
      { mimeType: "image/webp", friendlyName: "webp" },
    ],
    [MediaKind.Video]: [
      { mimeType: "video/mp4", friendlyName: "mp4" },
      { mimeType: "video/quicktime", friendlyName: "mov" },
      { mimeType: "video/webm", friendlyName: "webm" },
    ],
  };

  private static supportsDirectUpload: Record<MediaKind, boolean> = {
    [MediaKind.Image]: true,
    [MediaKind.Video]: true,
  };

  private static maxFileSizes: Record<MediaKind, number> = {
    [MediaKind.Image]: 10 * 1024 * 1024, // 10MB
    [MediaKind.Video]: 50 * 1024 * 1024, // 50MB
  };

  private static supportedPrefixes: Record<MediaKind, string[]> = {
    [MediaKind.Image]: ["image/"],
    [MediaKind.Video]: ["video/"],
  };

  static getRenderer(kind: MediaKind): MediaRendererComponent {
    return this.renderers[kind];
  }

  static validateFile(file: File, kind: MediaKind): boolean {
    return this.validators[kind](file);
  }

  static getAcceptedTypes(kind: MediaKind): string {
    return this.acceptedMimeTypes[kind].map((info) => info.mimeType).join(",");
  }

  static setVideoEnabled(enabled: boolean): void {
    this.videoEnabled = enabled;
  }

  static isVideoEnabled(): boolean {
    return this.videoEnabled;
  }

  static getAcceptedMimeTypes(kind: MediaKind): string[] {
    // Return empty array for video types when video is disabled
    if (kind === MediaKind.Video && !this.videoEnabled) {
      return [];
    }
    return this.acceptedMimeTypes[kind].map((info) => info.mimeType);
  }

  static getAllAcceptedMimeTypes(): string[] {
    const kinds = Object.values(MediaKind) as MediaKind[];
    return kinds.flatMap((kind) => {
      // Skip video types when video is disabled
      if (kind === MediaKind.Video && !this.videoEnabled) {
        return [];
      }
      return this.acceptedMimeTypes[kind].map((info) => info.mimeType);
    });
  }

  static getAllFriendlyNames(): string[] {
    const kinds = Object.values(MediaKind) as MediaKind[];
    return kinds.flatMap((kind) => {
      // Skip video types when video is disabled
      if (kind === MediaKind.Video && !this.videoEnabled) {
        return [];
      }
      return this.acceptedMimeTypes[kind].map((info) => info.friendlyName);
    });
  }

  static getMaxFileSize(kind: MediaKind): number {
    return this.maxFileSizes[kind];
  }

  static getSupportsDirectUpload(kind: MediaKind): boolean {
    return this.supportsDirectUpload[kind];
  }

  static getSupportedPrefixes(kind: MediaKind): string[] {
    return this.supportedPrefixes[kind];
  }

  static getAllSupportedPrefixes(): string[] {
    const kinds = Object.values(MediaKind) as MediaKind[];
    return kinds.flatMap((kind) => {
      // Skip video types when video is disabled
      if (kind === MediaKind.Video && !this.videoEnabled) {
        return [];
      }
      return this.supportedPrefixes[kind];
    });
  }

  /**
   * Returns accepted mime types filtered by model capabilities.
   * @param mediaCapabilities Array of media types the model supports (e.g., [MediaKind.Image] or [MediaKind.Image, MediaKind.Video])
   */
  static getAcceptedMimeTypesForCapabilities(mediaCapabilities: MediaKind[]): string[] {
    const mimeTypes: string[] = [];

    if (mediaCapabilities.includes(MediaKind.Image)) {
      mimeTypes.push(...this.getAcceptedMimeTypes(MediaKind.Image));
    }

    if (mediaCapabilities.includes(MediaKind.Video) && this.videoEnabled) {
      mimeTypes.push(...this.getAcceptedMimeTypes(MediaKind.Video));
    }

    return mimeTypes;
  }

  static async processFile(file: File, kind: MediaKind): Promise<MediaData> {
    if (!this.validateFile(file, kind)) {
      throw new Error(`Invalid file type for ${kind}`);
    }

    if (file.size > this.getMaxFileSize(kind)) {
      throw new Error(`File too large. Max size: ${this.getMaxFileSize(kind) / (1024 * 1024)}MB`);
    }

    if (kind === MediaKind.Video) {
      throw new Error("Videos should be uploaded directly to S3, not processed as base64");
    }

    const base64 = await this.convertToBase64(file);
    const base64Data = base64.split(",")[1] || base64;

    return {
      data: base64Data,
      extension: getExtensionFromContentType(file.type),
      key: generateUUID(),
      documentIndex: null,
    };
  }

  static detectMediaKind(media: MediaData): MediaKind {
    const contentType = getContentTypeFromExtension(media.extension);
    if (contentType.startsWith("image/")) {
      return MediaKind.Image;
    } else if (contentType.startsWith("video/")) {
      return MediaKind.Video;
    }

    // Default fallback
    return MediaKind.Image;
  }

  static detectMediaKindFromFile(file: File): MediaKind | null {
    // Check against our accepted MIME types
    for (const [kind, mediaTypeInfos] of Object.entries(this.acceptedMimeTypes)) {
      const mediaKind = kind as MediaKind;
      // Skip video types when video is disabled
      if (mediaKind === MediaKind.Video && !this.videoEnabled) {
        continue;
      }
      if (mediaTypeInfos.some((info) => info.mimeType === file.type)) {
        return mediaKind;
      }
    }

    return null;
  }

  private static convertToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
}
